/**
 * AffiliateLeaderboard — public "Top Earners" widget.
 *
 * Renders network-wide stats banner + ranked list of top 10 affiliates
 * (Gold 1-3 · Silver 4-6 · Bronze 7-10). Names are server-anonymized
 * (`John S.`) and vanity codes shown only if the partner opted into one.
 *
 * Drop-in: `<AffiliateLeaderboard limit={10} />` — fetches from
 * `GET /api/affiliate/leaderboard` on mount. Silently renders nothing
 * if the network has zero conversions yet (graceful empty state).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Users, DollarSign, Sparkles } from "lucide-react";
import { API } from "../../config";

const BADGE_STYLE = {
  gold:   { bg: "bg-[#FFD700]/15", text: "text-[#FFD700]", ring: "ring-[#FFD700]/40", emoji: "🥇" },
  silver: { bg: "bg-[#C0C0C0]/15", text: "text-zinc-300",   ring: "ring-zinc-400/40", emoji: "🥈" },
  bronze: { bg: "bg-[#CD7F32]/15", text: "text-[#E2A878]", ring: "ring-[#CD7F32]/30", emoji: "🥉" },
};

const AUDIENCE_LABEL = {
  b2b: "B2B",
  ecommerce: "E-commerce",
  creators: "Creators",
  agency: "Agency",
  other: "Other",
};

export default function AffiliateLeaderboard({ limit = 10, t = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/affiliate/leaderboard?limit=${limit}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* hide silently on error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  if (loading) return null;
  if (!data || !data.leaders?.length) return null;

  const { leaders, network } = data;
  const fmtMoney = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <section
      data-testid="affiliate-leaderboard"
      className="max-w-5xl mx-auto px-4 pb-16"
    >
      <div className="border-2 border-zinc-800 bg-gradient-to-b from-[#141414] to-[#0a0a0a]">
        {/* Header */}
        <div className="border-b border-zinc-800 px-6 py-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFE600]/10 border border-[#FFE600]/30 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-[#FFE600]" />
            </div>
            <div>
              <h3 className="text-lg font-black font-['Cabinet_Grotesk']" data-testid="leaderboard-title">
                {t.title || "Top Earners — Live"}
              </h3>
              <p className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider">
                {t.subtitle || `${network.commission_rate_pct}% lifetime · auto-updated`}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-mono">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            LIVE
          </span>
        </div>

        {/* Network stats strip */}
        <div className="grid grid-cols-3 border-b border-zinc-800 divide-x divide-zinc-800">
          <Stat
            icon={Users}
            label={t.stat_partners || "Active partners"}
            value={network.active_partners.toLocaleString()}
            testid="leaderboard-stat-partners"
          />
          <Stat
            icon={DollarSign}
            label={t.stat_paid || "Paid to partners"}
            value={fmtMoney(network.total_paid_eur)}
            testid="leaderboard-stat-paid"
          />
          <Stat
            icon={Sparkles}
            label={t.stat_conv || "Tracked sales"}
            value={network.total_conversions.toLocaleString()}
            testid="leaderboard-stat-conversions"
          />
        </div>

        {/* Ranked list */}
        <ol className="divide-y divide-zinc-800" data-testid="leaderboard-list">
          {leaders.map((row) => {
            const style = BADGE_STYLE[row.badge] || BADGE_STYLE.bronze;
            return (
              <li
                key={row.rank}
                data-testid={`leaderboard-row-${row.rank}`}
                className="grid grid-cols-12 gap-3 items-center px-6 py-4 hover:bg-zinc-900/40 transition-colors"
              >
                <div className="col-span-1 flex items-center">
                  <span
                    className={`w-9 h-9 rounded-full ring-1 ${style.ring} ${style.bg} ${style.text} flex items-center justify-center font-black text-sm font-mono`}
                    title={row.badge}
                  >
                    {row.rank}
                  </span>
                </div>
                <div className="col-span-5 sm:col-span-4">
                  <p className="text-sm font-bold truncate">
                    <span className="mr-1">{style.emoji}</span>
                    {row.display_name}
                  </p>
                  {row.vanity ? (
                    <p className="text-[11px] text-zinc-500 font-mono truncate">/r/{row.vanity}</p>
                  ) : (
                    <p className="text-[11px] text-zinc-600 font-mono truncate">anonymous</p>
                  )}
                </div>
                <div className="hidden sm:block sm:col-span-2 text-[11px] text-zinc-500 font-mono uppercase tracking-wider">
                  {AUDIENCE_LABEL[row.audience_type] || "Other"}
                </div>
                <div className="col-span-3 sm:col-span-2 text-right text-xs text-zinc-400 font-mono">
                  {row.conversions} {row.conversions === 1 ? (t.sale || "sale") : (t.sales || "sales")}
                </div>
                <div className="col-span-3 sm:col-span-3 text-right">
                  <span className={`font-black text-base ${style.text}`} data-testid={`leaderboard-earned-${row.rank}`}>
                    {fmtMoney(row.earned_eur)}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>

        {/* CTA strip */}
        <div className="border-t border-zinc-800 px-6 py-4 text-center">
          <p className="text-xs text-zinc-500">
            {t.cta_strip || `Join ${network.active_partners}+ partners earning ${network.commission_rate_pct}% lifetime on every referral.`}
          </p>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="px-5 py-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-black font-['Cabinet_Grotesk'] text-white">{value}</p>
    </div>
  );
}
